// File: src/pages/Menu/MenuItemsPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Loader,
  RefreshCcw,
  Search,
  ChevronDown,
  ChevronUp,
  PackageOpen,
  Edit3,
  Trash2,
  Plus,
  Save,
  X,
  AlertTriangle,
  Wand2,
  CheckCircle2,
  Download,
  FileSpreadsheet,
  FileText,
  CheckSquare,
  Square,
} from "lucide-react";
import api from "@/lib/axios"; // baseURL -> /solo-mdsh-api

function useDebounced(v, ms = 300) {
  const [val, setVal] = useState(v);
  useEffect(() => {
    const t = setTimeout(() => setVal(v), ms);
    return () => clearTimeout(t);
  }, [v, ms]);
  return val;
}

const asNum = (v) => {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string") {
    const n = Number(v.replace(/[^0-9.\-]/g, ""));
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

const money = (v) => {
  const n = asNum(v);
  return n === null ? "—" : n.toLocaleString("en-GB", { style: "currency", currency: "GBP" });
};

const COLS = [
  { key: "name", label: "Name", sortable: true },
  { key: "description", label: "Description", sortable: true },
  { key: "auto_cost", label: "Auto Cost", sortable: true, align: "right" },
  { key: "actual_price", label: "Selling Price", sortable: true, align: "right" },
  { key: "food_cost", label: "Food Cost %", sortable: true, align: "right" },
  { key: "actions", label: "Actions", sortable: false, align: "right" },
];

export default function MenuItemsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const [q, setQ] = useState("");
  const dq = useDebounced(q, 300);
  const [sort, setSort] = useState("name");
  const [dir, setDir] = useState("asc");

  const [openIds, setOpenIds] = useState(new Set());
  const [selectedIds, setSelectedIds] = useState(new Set());

  const [ingredientsCache, setIngredientsCache] = useState({});
  const [ingEdits, setIngEdits] = useState({});
  const [addIngDraft, setAddIngDraft] = useState({});

  // { [menuItemId]: string[] | {mode:'details', declared: any[], suggested: any[]} | 'loading' | 'error' }
  const [allergensCache, setAllergensCache] = useState({});

  // { [item_id]: { state: 'idle'|'loading'|'ready'|'error', names: string[], ids: number[] } }
  const [suggestCache, setSuggestCache] = useState({});

  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkLimit, setBulkLimit] = useState(200);
  const [bulkOffset, setBulkOffset] = useState(0);
  const [bulkDryRun, setBulkDryRun] = useState(true);
  const [bulkReplace, setBulkReplace] = useState(false);
  const [bulkWorking, setBulkWorking] = useState(false);
  const [bulkMsg, setBulkMsg] = useState("");

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);

  const fetchList = async () => {
    const params = {};
    if (dq.trim()) params.q = dq.trim();

    const { data } = await api.get("/inventory/menu-items", { params });

    const list = Array.isArray(data)
      ? data
      : Array.isArray(data?.rows)
      ? data.rows
      : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data?.data)
      ? data.data
      : [];

    return list.map((r) => {
      const aggAuto = asNum(r.auto_cost) ?? 0;
      const manualOverride = asNum(r.manual_cost_override);
      const effectiveCost = manualOverride ?? aggAuto;
      const actual = asNum(r.manual_sale_price_override);
      const foodCost = actual && actual > 0 ? (effectiveCost / actual) * 100 : null;

      return {
        _raw: r,
        id: r.menu_item_id ?? r.id ?? r.item_id ?? r.uid,
        menu_item_id: r.menu_item_id ?? r.id ?? r.item_id ?? r.uid,
        name: r.name ?? r.menu_item_name ?? "—",
        description: r.description ?? "",
        auto_cost: effectiveCost,
        base_auto_cost: aggAuto,
        manual_cost_override: manualOverride,
        actual_price: actual,
        food_cost: foodCost,
        default_serving_size: r.default_serving_size ?? 1,
        manual_sale_price_override: r.manual_sale_price_override ?? null,
      };
    });
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");

    (async () => {
      try {
        const mapped = await fetchList();
        if (!cancelled) setRows(mapped);
      } catch (e) {
        console.error("Menu items fetch failed:", e);
        if (!cancelled) {
          setRows([]);
          setError(e?.response?.data?.error || "Failed to load menu items.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dq]);

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      const mapped = await fetchList();
      setRows(mapped);

      const ids = Array.from(openIds);
      await Promise.all(ids.map((id) => fetchAllergens(id, true)));
    } finally {
      setRefreshing(false);
    }
  };

  const sorted = useMemo(() => {
    const dirMul = dir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = a[sort];
      const bv = b[sort];
      if (["auto_cost", "actual_price", "food_cost"].includes(sort)) {
        const an = av ?? -Infinity;
        const bn = bv ?? -Infinity;
        return (an - bn) * dirMul;
      }
      return String(av ?? "").localeCompare(String(bv ?? "")) * dirMul;
    });
  }, [rows, sort, dir]);

  const selectedCount = selectedIds.size;

  const allVisibleSelected =
    sorted.length > 0 && sorted.every((row) => selectedIds.has(row.menu_item_id));

  const someVisibleSelected =
    sorted.some((row) => selectedIds.has(row.menu_item_id)) && !allVisibleSelected;

  const toggleSelectOne = (menuItemId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(menuItemId)) next.delete(menuItemId);
      else next.add(menuItemId);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        sorted.forEach((row) => next.delete(row.menu_item_id));
      } else {
        sorted.forEach((row) => next.add(row.menu_item_id));
      }
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  const header = (col) => {
    const active = sort === col.key;
    const align =
      col.align === "right" ? "text-right" : col.align === "center" ? "text-center" : "text-left";
    return (
      <th
        key={col.key}
        scope="col"
        className={`px-4 py-3 text-xs font-semibold tracking-wide ${
          col.sortable ? "cursor-pointer select-none" : ""
        } ${align}`}
        onClick={() => {
          if (!col.sortable) return;
          if (active) setDir((d) => (d === "asc" ? "desc" : "asc"));
          else {
            setSort(col.key);
            setDir("asc");
          }
        }}
        title={col.sortable ? "Sort" : undefined}
      >
        <span className="inline-flex items-center gap-1">
          {col.label}
          {col.sortable && (
            <span className={`text-[10px] ${active ? "text-emerald-300" : "text-white/30"}`}>
              {active ? (dir === "asc" ? "▲" : "▼") : "↕"}
            </span>
          )}
        </span>
      </th>
    );
  };

  const fetchAllergens = async (menuItemId, force = false) => {
    if (!force && allergensCache[menuItemId] && allergensCache[menuItemId] !== "error") return;

    setAllergensCache((m) => ({ ...m, [menuItemId]: "loading" }));
    try {
      const { data } = await api.get(`/inventory/menu-items/${menuItemId}/allergens`, {
        params: { details: 1 },
      });

      if (data && typeof data === "object" && !Array.isArray(data)) {
        const declared = Array.isArray(data.declared) ? data.declared : [];
        const suggested = Array.isArray(data.suggested) ? data.suggested : [];

        setAllergensCache((m) => ({
          ...m,
          [menuItemId]: { mode: "details", declared, suggested },
        }));
        return;
      }

      if (Array.isArray(data) && data.length > 0 && typeof data[0] === "object") {
        const declared = [];
        const suggested = [];
        data.forEach((r) => {
          const src = String(r?.source || "manual").toLowerCase();
          if (src === "suggested") suggested.push(r);
          else declared.push(r);
        });

        setAllergensCache((m) => ({
          ...m,
          [menuItemId]: { mode: "details", declared, suggested },
        }));
        return;
      }

      if (Array.isArray(data)) {
        setAllergensCache((m) => ({ ...m, [menuItemId]: data }));
        return;
      }

      setAllergensCache((m) => ({ ...m, [menuItemId]: [] }));
    } catch (e) {
      try {
        const { data } = await api.get(`/inventory/menu-items/${menuItemId}/allergens`);
        const list = Array.isArray(data) ? data : Array.isArray(data?.allergens) ? data.allergens : [];
        setAllergensCache((m) => ({ ...m, [menuItemId]: list }));
      } catch (e2) {
        console.error("Allergen fetch failed:", e2);
        setAllergensCache((m) => ({ ...m, [menuItemId]: "error" }));
      }
    }
  };

  const toggleOpen = async (id) => {
    const next = new Set(openIds);
    if (next.has(id)) {
      next.delete(id);
      setOpenIds(next);
      return;
    }
    next.add(id);
    setOpenIds(next);

    if (!ingredientsCache[id]) {
      setIngredientsCache((m) => ({ ...m, [id]: "loading" }));
      try {
        const { data } = await api.get(`/inventory/menu-items/${id}`);
        const ings = Array.isArray(data?.ingredients) ? data.ingredients : [];

        const seedEdits = {};
        ings.forEach((ing) => {
          seedEdits[ing.menu_item_ingredient_id] = {
            quantity_required: ing.quantity_required ?? ing.quantity ?? "",
            unit_cost_override: ing.unit_cost_override ?? "",
          };
        });

        setIngEdits((prev) => ({ ...prev, [id]: seedEdits }));
        setIngredientsCache((m) => ({ ...m, [id]: ings }));
        setAddIngDraft((prev) => ({
          ...prev,
          [id]: { item_id: "", quantity_required: "", unit_cost_override: "" },
        }));
      } catch (e) {
        console.error("Ingredient fetch failed:", e);
        setIngredientsCache((m) => ({ ...m, [id]: "error" }));
      }
    }

    fetchAllergens(id, true);
  };

  const openEdit = (row) => {
    setEditItem({
      menu_item_id: row.menu_item_id,
      name: row.name || "",
      description: row.description || "",
      default_serving_size: row.default_serving_size ?? 1,
      manual_cost_override: row.manual_cost_override ?? "",
      manual_sale_price_override: row.manual_sale_price_override ?? "",
    });
    setEditModalOpen(true);
  };

  const saveEdit = async () => {
    if (!editItem) return;
    setEditSaving(true);
    try {
      const payload = {
        name: editItem.name,
        description: editItem.description || null,
        default_serving_size: Number(editItem.default_serving_size) || 1,
        manual_cost_override: editItem.manual_cost_override === "" ? null : Number(editItem.manual_cost_override),
        manual_sale_price_override:
          editItem.manual_sale_price_override === "" ? null : Number(editItem.manual_sale_price_override),
      };
      await api.put(`/inventory/menu-items/${editItem.menu_item_id}`, payload);
      setEditModalOpen(false);
      setEditItem(null);
      await onRefresh();
    } catch (e) {
      console.error("Failed to save menu item:", e);
      alert("Failed to save changes.");
    } finally {
      setEditSaving(false);
    }
  };

  const deleteItem = async (row) => {
    if (!confirm(`Delete "${row.name}"? This will remove its ingredients too.`)) return;
    try {
      await api.delete(`/inventory/menu-items/${row.menu_item_id}`);
      setOpenIds((prev) => {
        const n = new Set(prev);
        n.delete(row.menu_item_id);
        return n;
      });
      setSelectedIds((prev) => {
        const n = new Set(prev);
        n.delete(row.menu_item_id);
        return n;
      });
      setIngredientsCache((m) => {
        const c = { ...m };
        delete c[row.menu_item_id];
        return c;
      });
      setAllergensCache((m) => {
        const c = { ...m };
        delete c[row.menu_item_id];
        return c;
      });
      await onRefresh();
    } catch (e) {
      console.error("Delete failed:", e);
      alert("Failed to delete menu item.");
    }
  };

  const setIngField = (menuItemId, ingId, patch) => {
    setIngEdits((prev) => ({
      ...prev,
      [menuItemId]: {
        ...(prev[menuItemId] || {}),
        [ingId]: { ...(prev[menuItemId]?.[ingId] || {}), ...patch },
      },
    }));
  };

  const rehydrateIngs = async (menuItemId) => {
    setIngredientsCache((m) => ({ ...m, [menuItemId]: "loading" }));
    const { data } = await api.get(`/inventory/menu-items/${menuItemId}`);
    const ings = Array.isArray(data?.ingredients) ? data.ingredients : [];
    const seedEdits = {};
    ings.forEach((ing) => {
      seedEdits[ing.menu_item_ingredient_id] = {
        quantity_required: ing.quantity_required ?? ing.quantity ?? "",
        unit_cost_override: ing.unit_cost_override ?? "",
      };
    });
    setIngEdits((prev) => ({ ...prev, [menuItemId]: seedEdits }));
    setIngredientsCache((m) => ({ ...m, [menuItemId]: ings }));
  };

  const suggestForStockItem = async (stockItemId) => {
    if (!stockItemId) return;
    setSuggestCache((m) => ({ ...m, [stockItemId]: { state: "loading", names: [], ids: [] } }));
    try {
      const { data } = await api.get(`/inventory/stock-items/${stockItemId}/allergens/suggest`);
      const names = Array.isArray(data?.suggested_names) ? data.suggested_names : [];
      const ids = Array.isArray(data?.suggested_ids) ? data.suggested_ids : [];
      setSuggestCache((m) => ({ ...m, [stockItemId]: { state: "ready", names, ids } }));
    } catch (e) {
      console.error("Suggest fetch failed:", e);
      setSuggestCache((m) => ({ ...m, [stockItemId]: { state: "error", names: [], ids: [] } }));
    }
  };

  const applySuggestedAllergens = async (menuItemId, stockItemId) => {
    const entry = suggestCache?.[stockItemId];
    if (!entry || entry.state !== "ready") return;
    try {
      await api.put(`/inventory/stock-items/${stockItemId}/allergens`, {
        allergen_ids: entry.ids,
        risk_level: "contains",
        source: "suggested",
        confidence: 0.7,
      });

      await fetchAllergens(menuItemId, true);
      setSuggestCache((m) => ({ ...m, [stockItemId]: { state: "idle", names: [], ids: [] } }));
      await rehydrateIngs(menuItemId);
      await onRefresh();
    } catch (e) {
      console.error("Apply suggestions failed:", e);
      alert(e?.response?.data?.error || "Failed to apply suggested allergens.");
    }
  };

  const runBulkSuggestApply = async () => {
    setBulkWorking(true);
    setBulkMsg("");
    try {
      const { data } = await api.post(`/inventory/stock-items/allergens/suggest/apply/bulk`, {
        limit: Number(bulkLimit) || 200,
        offset: Number(bulkOffset) || 0,
        dry_run: !!bulkDryRun,
        replace: !!bulkReplace,
        risk_level: "contains",
        source: "suggested",
        confidence: 0.6,
      });

      const count = Number(data?.count || 0);
      setBulkMsg(
        data?.dry_run
          ? `Dry-run done. ${count} items scanned. (No DB changes)`
          : `Applied suggestions. ${count} items processed.`
      );

      if (!data?.dry_run) {
        const ids = Array.from(openIds);
        await Promise.all(ids.map((id) => fetchAllergens(id, true)));
      }
    } catch (e) {
      console.error("Bulk apply failed:", e);
      setBulkMsg(e?.response?.data?.error || "Bulk apply failed.");
    } finally {
      setBulkWorking(false);
    }
  };

  const saveIngredient = async (menuItemId, ingId) => {
    const buf = ingEdits?.[menuItemId]?.[ingId];
    if (!buf) return;
    try {
      const payload = {
        quantity_required: buf.quantity_required === "" ? undefined : Number(buf.quantity_required),
        unit_cost_override: buf.unit_cost_override === "" ? null : Number(buf.unit_cost_override),
      };
      await api.patch(`/inventory/menu-items/${menuItemId}/ingredients/${ingId}`, payload);
      await rehydrateIngs(menuItemId);
      await fetchAllergens(menuItemId, true);
      await onRefresh();
    } catch (e) {
      console.error("Save ingredient failed:", e);
      alert("Failed to save ingredient changes.");
    }
  };

  const removeIngredient = async (menuItemId, ingId) => {
    if (!confirm("Remove this ingredient from the dish?")) return;
    try {
      await api.delete(`/inventory/menu-items/${menuItemId}/ingredients/${ingId}`);
      await rehydrateIngs(menuItemId);
      await fetchAllergens(menuItemId, true);
      await onRefresh();
    } catch (e) {
      console.error("Delete ingredient failed:", e);
      alert("Failed to delete ingredient.");
    }
  };

  const setAddDraft = (menuItemId, patch) => {
    setAddIngDraft((prev) => ({ ...prev, [menuItemId]: { ...(prev[menuItemId] || {}), ...patch } }));
  };

  const addIngredient = async (menuItemId) => {
    const draft = addIngDraft?.[menuItemId] || {};
    if (!draft.item_id || !draft.quantity_required) {
      alert("Please enter item_id and quantity_required.");
      return;
    }
    try {
      const payload = {
        item_id: Number(draft.item_id),
        quantity_required: Number(draft.quantity_required),
        unit_cost_override: draft.unit_cost_override === "" ? null : Number(draft.unit_cost_override),
      };
      await api.post(`/inventory/menu-items/${menuItemId}/ingredients`, payload);
      setAddDraft(menuItemId, { item_id: "", quantity_required: "", unit_cost_override: "" });
      await rehydrateIngs(menuItemId);
      await fetchAllergens(menuItemId, true);
      await onRefresh();
    } catch (e) {
      console.error("Add ingredient failed:", e);
      alert("Failed to add ingredient.");
    }
  };

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const buildExportPayload = () => {
    const menuItemIds = Array.from(selectedIds);
    return {
      menu_item_ids: menuItemIds,
      include_allergens: true,
      include_allergen_sources: true,
      include_ingredients: true,
      include_prices: false,
      include_costs: false,
    };
  };

  const exportSelectedPdf = async () => {
    if (!selectedIds.size) {
      alert("Please select at least one dish or drink to export.");
      return;
    }

    setExportingPdf(true);
    try {
      const payload = buildExportPayload();
      const response = await api.post("/inventory/menu-items/export/pdf", payload, {
        responseType: "blob",
      });

      downloadBlob(response.data, "Selected_Menu_Items.pdf");
    } catch (e) {
      console.error("PDF export failed:", e);
      alert(e?.response?.data?.error || "Failed to export PDF.");
    } finally {
      setExportingPdf(false);
    }
  };

  const exportSelectedExcel = async () => {
    if (!selectedIds.size) {
      alert("Please select at least one dish or drink to export.");
      return;
    }

    setExportingExcel(true);
    try {
      const payload = buildExportPayload();
      const response = await api.post("/inventory/menu-items/export/excel", payload, {
        responseType: "blob",
      });

      downloadBlob(response.data, "Selected_Menu_Items.xlsx");
    } catch (e) {
      console.error("Excel export failed:", e);
      alert(e?.response?.data?.error || "Failed to export Excel.");
    } finally {
      setExportingExcel(false);
    }
  };

  const pillStyleForRisk = (risk) => {
    const r = String(risk || "contains").toLowerCase();
    if (r === "may_contain") return "bg-yellow-100 text-yellow-800 border-yellow-200";
    if (r === "trace") return "bg-slate-100 text-slate-800 border-slate-200";
    if (r === "unknown") return "bg-gray-100 text-gray-800 border-gray-200";
    return "bg-amber-100 text-amber-800 border-amber-200";
  };

  const allergenSourceLabel = (row) => {
    const itemCode = String(row?.item_code || "").trim();
    const itemName = String(row?.item_name || row?.stock_description || "").trim();

    if (itemCode && itemName) return `${itemCode} — ${itemName}`;
    if (itemName) return itemName;
    if (itemCode) return itemCode;
    return "Unknown item";
  };

  const AllergenPills = ({ list }) => {
    if (list === "loading") {
      return (
        <span className="text-gray-700 text-xs inline-flex items-center gap-2">
          <Loader className="w-3 h-3 animate-spin" />
          Loading allergens…
        </span>
      );
    }
    if (list === "error") return <span className="text-xs text-rose-600">Failed to load allergens.</span>;
    if (!list) return <span className="text-xs text-gray-500">—</span>;

    if (typeof list === "object" && list.mode === "details") {
      const declared = Array.isArray(list.declared) ? list.declared : [];
      const suggested = Array.isArray(list.suggested) ? list.suggested : [];

      const renderGroup = (title, rows, tint) => (
        <div className="mb-3">
          <div className={`text-[11px] font-semibold ${tint} mb-1.5`}>{title}</div>
          {rows.length === 0 ? (
            <div className="text-[11px] text-gray-500">—</div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {rows.map((r, idx) => {
                const name = r?.name || "—";
                const risk = String(r?.risk_level || "contains").toLowerCase();
                const src = String(r?.source || "").toLowerCase();
                const conf = r?.confidence;
                const fromLabel = allergenSourceLabel(r);

                const badge =
                  risk === "may_contain"
                    ? "May contain"
                    : risk === "trace"
                    ? "Trace"
                    : risk === "unknown"
                    ? "Unknown"
                    : "Contains";

                return (
                  <div
                    key={`${title}-${name}-${risk}-${src}-${r?.item_id ?? idx}`}
                    className={`rounded-lg border px-2.5 py-1.5 ${pillStyleForRisk(risk)}`}
                    title={`${badge}${src ? ` • source: ${src}` : ""}${
                      conf != null ? ` • confidence: ${Number(conf).toFixed(2)}` : ""
                    }${fromLabel ? ` • item: ${fromLabel}` : ""}`}
                  >
                    <div className="text-[11px] font-semibold leading-tight">
                      {name} <span className="ml-1 text-[10px] opacity-70">({badge})</span>
                    </div>
                    <div className="text-[10px] opacity-80 mt-0.5 leading-tight">
                      From: {fromLabel}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );

      return (
        <div>
          {renderGroup("Declared (manual / catalog / barcode)", declared, "text-emerald-700")}
          {renderGroup("Suggested (AI / keyword)", suggested, "text-violet-700")}
        </div>
      );
    }

    if (!Array.isArray(list) || list.length === 0) return <span className="text-xs text-gray-500">—</span>;
    return (
      <div className="flex flex-wrap gap-1.5">
        {list.map((name) => (
          <span
            key={name}
            className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200"
            title="Derived from stock item allergens"
          >
            {name}
          </span>
        ))}
      </div>
    );
  };

  const Suggestor = ({ menuItemId, stockItemId }) => {
    const entry = suggestCache[stockItemId];
    return (
      <div className="mt-1 flex items-center gap-2">
        <button
          onClick={() => suggestForStockItem(stockItemId)}
          className="px-2 py-1 rounded bg-violet-600 hover:bg-violet-700 text-white inline-flex items-center gap-1"
          title="Suggest allergens from text"
        >
          {entry?.state === "loading" ? <Loader className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
          Suggest
        </button>

        {entry?.state === "ready" && (
          <>
            <div className="flex flex-wrap gap-1.5">
              {entry.names.length === 0 ? (
                <span className="text-xs text-gray-600">No suggestions.</span>
              ) : (
                entry.names.map((n) => (
                  <span
                    key={n}
                    className="text-[11px] px-2 py-0.5 rounded-full bg-violet-100 text-violet-800 border border-violet-200"
                  >
                    {n}
                  </span>
                ))
              )}
            </div>

            {entry.ids.length > 0 && (
              <button
                onClick={() => applySuggestedAllergens(menuItemId, stockItemId)}
                className="px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center gap-1"
                title="Apply suggestions to this stock item"
              >
                <CheckCircle2 className="w-4 h-4" />
                Apply
              </button>
            )}
          </>
        )}

        {entry?.state === "error" && <span className="text-xs text-rose-600">Failed to suggest.</span>}
      </div>
    );
  };

  return (
    <div className="min-h-screen p-6 bg-gradient-to-br from-black via-gray-900 to-gray-950 text-white">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="text-2xl font-extrabold">📖 Menu Items</h1>
          <p className="text-gray-400 text-sm">List of dishes & drinks with quick edit tools.</p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setBulkOpen((v) => !v)}
            className="bg-violet-700 hover:bg-violet-600 px-3 py-2 rounded inline-flex items-center gap-2"
            title="Layer 2: bulk suggest/apply allergens"
          >
            <Wand2 className="w-4 h-4" />
            Bulk Allergens
          </button>

          <button
            onClick={onRefresh}
            disabled={refreshing}
            className="bg-gray-800 hover:bg-gray-700 px-3 py-2 rounded inline-flex items-center gap-2 disabled:opacity-50"
          >
            {refreshing ? <Loader className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
            Refresh
          </button>
        </div>
      </div>

      {bulkOpen && (
        <div className="mb-4 rounded-xl border border-white/10 bg-gray-900/50 p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="text-sm font-semibold">Layer 2 • Bulk suggestions</div>

            <label className="text-xs text-gray-300 flex items-center gap-2">
              Limit
              <input
                type="number"
                min="1"
                max="1000"
                value={bulkLimit}
                onChange={(e) => setBulkLimit(e.target.value)}
                className="w-24 bg-black/40 border border-white/10 rounded px-2 py-1 text-white"
              />
            </label>

            <label className="text-xs text-gray-300 flex items-center gap-2">
              Offset
              <input
                type="number"
                min="0"
                value={bulkOffset}
                onChange={(e) => setBulkOffset(e.target.value)}
                className="w-24 bg-black/40 border border-white/10 rounded px-2 py-1 text-white"
              />
            </label>

            <label className="text-xs text-gray-300 flex items-center gap-2">
              <input type="checkbox" checked={bulkDryRun} onChange={(e) => setBulkDryRun(e.target.checked)} />
              Dry-run (no DB writes)
            </label>

            <label className="text-xs text-gray-300 flex items-center gap-2">
              <input type="checkbox" checked={bulkReplace} onChange={(e) => setBulkReplace(e.target.checked)} />
              Replace existing mappings
            </label>

            <button
              onClick={runBulkSuggestApply}
              disabled={bulkWorking}
              className="ml-auto px-3 py-2 rounded bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center gap-2 disabled:opacity-50"
            >
              {bulkWorking ? <Loader className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Run
            </button>
          </div>

          {bulkMsg && <div className="mt-2 text-xs text-gray-200">{bulkMsg}</div>}
          <div className="mt-1 text-[11px] text-gray-400">Tip: run Dry-run first, then untick Dry-run to apply.</div>
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            placeholder="Search by name or description…"
            className="w-full pl-9 pr-3 py-2 rounded bg-gray-900/60 border border-white/10"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>

        <div className="text-xs text-gray-400">{rows.length} item(s)</div>
      </div>

      <div className="mb-4 rounded-xl border border-white/10 bg-gray-900/50 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="text-sm font-semibold text-white inline-flex items-center gap-2">
            <Download className="w-4 h-4" />
            Export Selected Menu Items
          </div>

          <button
            onClick={toggleSelectAllVisible}
            className="px-3 py-2 rounded bg-slate-700 hover:bg-slate-600 text-white inline-flex items-center gap-2"
          >
            {allVisibleSelected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
            {allVisibleSelected ? "Unselect Visible" : "Select Visible"}
          </button>

          <button
            onClick={clearSelection}
            disabled={!selectedCount}
            className="px-3 py-2 rounded bg-gray-800 hover:bg-gray-700 text-white disabled:opacity-50"
          >
            Clear Selection
          </button>

          <div className="text-xs text-gray-300">
            {selectedCount} selected
            {someVisibleSelected ? " • partially selected" : ""}
          </div>

          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={exportSelectedExcel}
              disabled={!selectedCount || exportingExcel || exportingPdf}
              className="px-3 py-2 rounded bg-emerald-700 hover:bg-emerald-600 text-white inline-flex items-center gap-2 disabled:opacity-50"
            >
              {exportingExcel ? <Loader className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
              Export Excel
            </button>

            <button
              onClick={exportSelectedPdf}
              disabled={!selectedCount || exportingPdf || exportingExcel}
              className="px-3 py-2 rounded bg-red-700 hover:bg-red-600 text-white inline-flex items-center gap-2 disabled:opacity-50"
            >
              {exportingPdf ? <Loader className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
              Export PDF
            </button>
          </div>
        </div>

        <div className="mt-2 text-[11px] text-gray-400">
          Export will include selected dishes/drinks with ingredients, allergens, and allergen source items.
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-white/10 bg-white">
        <table className="min-w-full text-sm text-gray-900">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-3 py-3 w-10 text-center">
                <button
                  type="button"
                  onClick={toggleSelectAllVisible}
                  className="inline-flex items-center justify-center"
                  title={allVisibleSelected ? "Unselect visible items" : "Select visible items"}
                >
                  {allVisibleSelected ? (
                    <CheckSquare className="w-4 h-4 text-emerald-700" />
                  ) : (
                    <Square className={`w-4 h-4 ${someVisibleSelected ? "text-amber-700" : "text-gray-600"}`} />
                  )}
                </button>
              </th>
              <th className="px-4 py-3 w-10"></th>
              {COLS.map((c) => header(c))}
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={COLS.length + 2} className="px-4 py-8 text-center text-gray-600">
                  <Loader className="w-4 h-4 inline-block animate-spin mr-2" />
                  Loading…
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={COLS.length + 2} className="px-4 py-8 text-center text-red-600">
                  {error}
                </td>
              </tr>
            ) : sorted.length === 0 ? (
              <tr>
                <td colSpan={COLS.length + 2} className="px-4 py-10 text-center text-gray-600">
                  <div className="flex flex-col items-center gap-2">
                    <PackageOpen className="w-8 h-8" />
                    <div className="font-semibold">No items found</div>
                    <div className="text-sm">Try a different search.</div>
                  </div>
                </td>
              </tr>
            ) : (
              sorted.map((r) => {
                const open = openIds.has(r.id);
                const selected = selectedIds.has(r.menu_item_id);
                const cache = ingredientsCache[r.id];
                const allergens = allergensCache[r.id];
                const align = (key) =>
                  ["auto_cost", "actual_price", "food_cost"].includes(key) ? "text-right" : "text-left";

                return (
                  <React.Fragment key={r.id ?? r.name}>
                    <tr className={`odd:bg-white even:bg-gray-50 ${selected ? "ring-1 ring-emerald-200 bg-emerald-50/50" : ""}`}>
                      <td className="px-3 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleSelectOne(r.menu_item_id)}
                          className="w-4 h-4 cursor-pointer"
                        />
                      </td>

                      <td className="px-2 py-2">
                        <button
                          onClick={() => toggleOpen(r.id)}
                          className="p-1 rounded hover:bg-gray-200"
                          title={open ? "Hide ingredients" : "Show ingredients"}
                        >
                          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                      </td>

                      <td className={`px-4 py-2 ${align("name")}`}>{r.name}</td>
                      <td className={`px-4 py-2 ${align("description")}`}>{r.description || "—"}</td>

                      <td className={`px-4 py-2 ${align("auto_cost")}`}>
                        {money(r.auto_cost)}
                        {r.manual_cost_override != null && (
                          <span className="ml-2 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 align-middle">
                            <AlertTriangle className="w-3 h-3" /> override
                          </span>
                        )}
                      </td>

                      <td className={`px-4 py-2 ${align("actual_price")}`}>{money(r.actual_price)}</td>

                      <td className={`px-4 py-2 ${align("food_cost")}`}>
                        {r.food_cost == null ? "—" : `${r.food_cost.toFixed(1)}%`}
                      </td>

                      <td className="px-4 py-2 text-right">
                        <div className="inline-flex gap-2">
                          <button
                            onClick={() => openEdit(r)}
                            className="px-2 py-1 rounded bg-blue-600 hover:bg-blue-700 text-white inline-flex items-center gap-1"
                          >
                            <Edit3 className="w-4 h-4" /> Edit
                          </button>

                          <button
                            onClick={() => deleteItem(r)}
                            className="px-2 py-1 rounded bg-rose-600 hover:bg-rose-700 text-white inline-flex items-center gap-1"
                          >
                            <Trash2 className="w-4 h-4" /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>

                    {open && (
                      <tr className="bg-gray-50">
                        <td colSpan={COLS.length + 2} className="px-6 py-3">
                          <div className="mb-3">
                            <div className="text-xs text-gray-700 font-semibold mb-1">Allergens</div>
                            <AllergenPills list={allergens} />
                          </div>

                          {cache === "loading" ? (
                            <div className="text-gray-700 text-sm">
                              <Loader className="w-4 h-4 inline-block animate-spin mr-2" />
                              Loading ingredients…
                            </div>
                          ) : cache === "error" ? (
                            <div className="text-red-600 text-sm">Failed to load ingredients.</div>
                          ) : Array.isArray(cache) && cache.length > 0 ? (
                            <div className="overflow-x-auto">
                              <table className="min-w-full text-xs">
                                <thead>
                                  <tr className="text-gray-700">
                                    <th className="text-left px-2 py-1">Item</th>
                                    <th className="text-left px-2 py-1 w-32">Qty (required)</th>
                                    <th className="text-left px-2 py-1 w-36">Unit Override (£/unit)</th>
                                    <th className="text-right px-2 py-1 w-40">Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {cache.map((ing) => {
                                    const buf =
                                      ingEdits?.[r.id]?.[ing.menu_item_ingredient_id] || {
                                        quantity_required: ing.quantity_required ?? ing.quantity ?? "",
                                        unit_cost_override: ing.unit_cost_override ?? "",
                                      };

                                    return (
                                      <tr key={ing.menu_item_ingredient_id} className="border-t">
                                        <td className="px-2 py-1">
                                          {ing.item_code ? `${ing.item_code} — ` : ""}
                                          {ing.stock_description || ing.description || ing.name || "—"}

                                          <div className="text-[11px] text-gray-500">
                                            {ing.unit_size ? `Stock UoM: ${ing.unit_size}` : "UoM: —"}{" "}
                                            {ing.unit_price != null ? `• Price: ${money(ing.unit_price)}` : ""}
                                          </div>

                                          {ing.item_id && <Suggestor menuItemId={r.id} stockItemId={ing.item_id} />}
                                        </td>

                                        <td className="px-2 py-1">
                                          <input
                                            type="number"
                                            min="0"
                                            step="0.0001"
                                            value={buf.quantity_required}
                                            onChange={(e) =>
                                              setIngField(r.id, ing.menu_item_ingredient_id, {
                                                quantity_required: e.target.value,
                                              })
                                            }
                                            className="w-full bg-white border border-gray-300 rounded px-2 py-1"
                                          />
                                        </td>

                                        <td className="px-2 py-1">
                                          <input
                                            type="number"
                                            min="0"
                                            step="0.0001"
                                            value={buf.unit_cost_override}
                                            onChange={(e) =>
                                              setIngField(r.id, ing.menu_item_ingredient_id, {
                                                unit_cost_override: e.target.value,
                                              })
                                            }
                                            className="w-full bg-white border border-gray-300 rounded px-2 py-1"
                                            placeholder="optional"
                                          />
                                        </td>

                                        <td className="px-2 py-1 text-right">
                                          <div className="inline-flex gap-2">
                                            <button
                                              onClick={() => saveIngredient(r.id, ing.menu_item_ingredient_id)}
                                              className="px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center gap-1"
                                            >
                                              <Save className="w-4 h-4" /> Save
                                            </button>

                                            <button
                                              onClick={() => removeIngredient(r.id, ing.menu_item_ingredient_id)}
                                              className="px-2 py-1 rounded bg-rose-600 hover:bg-rose-700 text-white inline-flex items-center gap-1"
                                            >
                                              <Trash2 className="w-4 h-4" /> Remove
                                            </button>
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  })}

                                  <tr className="border-t bg-white/50">
                                    <td className="px-2 py-2">
                                      <div className="text-xs font-semibold">Add Ingredient (by item_id)</div>
                                      <input
                                        type="number"
                                        min="1"
                                        value={addIngDraft?.[r.id]?.item_id ?? ""}
                                        onChange={(e) => setAddDraft(r.id, { item_id: e.target.value })}
                                        placeholder="item_id (from Stock Items)"
                                        className="mt-1 w-48 bg-white border border-gray-300 rounded px-2 py-1"
                                      />
                                    </td>

                                    <td className="px-2 py-2">
                                      <input
                                        type="number"
                                        min="0"
                                        step="0.0001"
                                        value={addIngDraft?.[r.id]?.quantity_required ?? ""}
                                        onChange={(e) => setAddDraft(r.id, { quantity_required: e.target.value })}
                                        placeholder="Qty"
                                        className="w-full bg-white border border-gray-300 rounded px-2 py-1"
                                      />
                                    </td>

                                    <td className="px-2 py-2">
                                      <input
                                        type="number"
                                        min="0"
                                        step="0.0001"
                                        value={addIngDraft?.[r.id]?.unit_cost_override ?? ""}
                                        onChange={(e) => setAddDraft(r.id, { unit_cost_override: e.target.value })}
                                        placeholder="Override (optional)"
                                        className="w-full bg-white border border-gray-300 rounded px-2 py-1"
                                      />
                                    </td>

                                    <td className="px-2 py-2 text-right">
                                      <button
                                        onClick={() => addIngredient(r.id)}
                                        className="px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-700 text-white inline-flex items-center gap-1"
                                      >
                                        <Plus className="w-4 h-4" /> Add
                                      </button>
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <div className="text-gray-600 text-sm">No ingredients found.</div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {editModalOpen && editItem && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4">
          <div className="bg-[#0f0f0f] text-white border border-gray-800 rounded-xl w-full max-w-2xl">
            <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Edit Menu Item</h2>
              <button onClick={() => setEditModalOpen(false)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-400">Name *</span>
                <input
                  className="bg-[#161616] border border-gray-700 rounded-lg px-3 py-2 outline-none"
                  value={editItem.name}
                  onChange={(e) => setEditItem((p) => ({ ...p, name: e.target.value }))}
                />
              </label>

              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-400">Default Serving Size</span>
                <input
                  type="number"
                  min="1"
                  step="1"
                  className="bg-[#161616] border border-gray-700 rounded-lg px-3 py-2 outline-none"
                  value={editItem.default_serving_size}
                  onChange={(e) => setEditItem((p) => ({ ...p, default_serving_size: e.target.value }))}
                />
              </label>

              <label className="flex flex-col gap-1 md:col-span-2">
                <span className="text-xs text-gray-400">Description</span>
                <textarea
                  className="bg-[#161616] border border-gray-700 rounded-lg px-3 py-2 outline-none min-h-[80px]"
                  value={editItem.description}
                  onChange={(e) => setEditItem((p) => ({ ...p, description: e.target.value }))}
                />
              </label>

              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-400">Manual Cost Override (£)</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="bg-[#161616] border border-gray-700 rounded-lg px-3 py-2 outline-none"
                  value={editItem.manual_cost_override}
                  onChange={(e) => setEditItem((p) => ({ ...p, manual_cost_override: e.target.value }))}
                />
              </label>

              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-400">Manual Sale Price Override (£)</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="bg-[#161616] border border-gray-700 rounded-lg px-3 py-2 outline-none"
                  value={editItem.manual_sale_price_override}
                  onChange={(e) => setEditItem((p) => ({ ...p, manual_sale_price_override: e.target.value }))}
                />
              </label>
            </div>

            <div className="px-4 py-3 border-t border-gray-800 flex justify-end gap-2">
              <button
                onClick={() => setEditModalOpen(false)}
                className="px-3 py-2 bg-[#161616] border border-gray-700 rounded-lg"
              >
                Cancel
              </button>

              <button
                disabled={editSaving}
                onClick={saveEdit}
                className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 rounded-lg inline-flex items-center gap-2 disabled:opacity-60"
              >
                {editSaving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}